import { SearchIcon } from "@chakra-ui/icons";
import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
  ListItem,
  Skeleton,
  Text,
  UnorderedList,
  useOutsideClick,
} from "@chakra-ui/react";
import * as DateFns from "date-fns";
import { useCombobox } from "downshift";
import _ from "lodash";
import Markdown from "markdown-to-jsx";
import React from "react";
import { RiAddLine } from "react-icons/ri";
import { Glossary } from "src/components/Text/Glossary";
import { useSearchApplicantDuplicates } from "src/generated/hooks/form-service/queries";
import type { ApplicantDuplicate } from "src/generated/hooks/form-service/requests";
import { useFocus } from "src/hooks/useFocus";
import { PASSWORD_MANAGER_IGNORE } from "src/types/passwordManager";

const PAGE_SIZE = 5;
const MIN_SEARCH_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 350;
// Stable default so an omitted excludeIds prop does not change identity per render.
const NO_EXCLUDED_IDS: string[] = [];

interface StudentSelectInputProps {
  organizationId: string;
  placeholder?: string;
  label?: string;
  isDisabled?: boolean;
  onSelectedStudent?: (value: ApplicantDuplicate | null | undefined) => void;
  onCreateNew?: (searchValue: string) => void;
  // Person ids already linked (e.g. students already on the parent). Shown in the
  // results but disabled/unselectable, so the admin sees they exist instead of
  // recreating them.
  excludeIds?: string[];
}

function toStudentName(
  student: ApplicantDuplicate | null | undefined,
  defaultValue: string = ""
): string {
  if (!student) return defaultValue;
  const parts = [student.first_name, student.last_name].filter(Boolean);
  return parts.join(" ") || defaultValue;
}

export function StudentSelectInput({
  organizationId,
  onSelectedStudent,
  onCreateNew,
  placeholder = "Search by student name",
  label = "Search by student name",
  isDisabled = false,
  excludeIds = NO_EXCLUDED_IDS,
}: StudentSelectInputProps) {
  const outsideRef = React.useRef<HTMLDivElement>(null);

  const [inputValue, setInputValue] = React.useState<string>("");
  const [students, setStudents] = React.useState<ApplicantDuplicate[]>([]);
  const [total, setTotal] = React.useState(0);
  const [offset, setOffset] = React.useState(0);
  const observerRef = React.useRef<HTMLDivElement | null>(null);

  const searchValue = inputValue.trim();

  const [debouncedSearchValue, setDebouncedSearchValue] =
    React.useState(searchValue);
  const debouncedSetSearchValue = React.useMemo(
    () =>
      _.debounce((value: string) => {
        setDebouncedSearchValue(value);
      }, SEARCH_DEBOUNCE_MS),
    []
  );
  React.useEffect(() => {
    debouncedSetSearchValue(searchValue);
    return () => debouncedSetSearchValue.cancel();
  }, [searchValue, debouncedSetSearchValue]);

  const {
    data: searchResponse,
    isLoading,
    isError: isSearchError,
  } = useSearchApplicantDuplicates(
    {
      query: {
        organizationId,
        search: debouncedSearchValue,
        limit: PAGE_SIZE,
        offset,
      },
    },
    undefined,
    {
      enabled:
        !!organizationId && debouncedSearchValue.length >= MIN_SEARCH_LENGTH,
    }
  );
  const data = searchResponse?.data;

  useOutsideClick({
    ref: outsideRef,
    handler: () => closeMenu(),
  });

  React.useEffect(() => {
    if (debouncedSearchValue.length < MIN_SEARCH_LENGTH) {
      setStudents([]);
      setTotal(0);
      setOffset(0);
      return;
    }
    if (!data) return;
    if (offset === 0) {
      setStudents(data.applicants);
    } else {
      setStudents((prev) => _.unionBy(prev, data.applicants, "id"));
    }
    setTotal(data.total);
  }, [data, offset, debouncedSearchValue]);

  React.useEffect(() => {
    setOffset(0);
  }, [debouncedSearchValue]);

  const fetchMore = React.useCallback(() => {
    if (students.length < total && searchValue.length >= MIN_SEARCH_LENGTH) {
      setOffset(students.length);
    }
  }, [students.length, total, searchValue]);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !isLoading && students.length > 0) {
          fetchMore();
        }
      },
      { rootMargin: "20px" }
    );
    const current = observerRef.current;
    if (current) observer.observe(current);
    return () => {
      if (current) observer.unobserve(current);
    };
  }, [students, isLoading, fetchMore]);

  const [isFocused, setIsFocused] = React.useState(false);
  const [inputRef, setInputFocus] = useFocus();
  React.useEffect(() => {
    if (isFocused) setInputFocus();
  }, [setInputFocus, isFocused]);

  React.useEffect(() => {
    if (inputRef.current) {
      inputRef.current.onblur = () => setIsFocused(false);
    }
  }, [inputRef, isFocused]);

  // Selecting a student emits it to the host (which owns the chip list) and clears
  // the search so the field is ready for the next lookup.
  const handleSelect = (student: ApplicantDuplicate) => {
    onSelectedStudent?.(student);
    setInputValue("");
    setDebouncedSearchValue("");
    setStudents([]);
    setOffset(0);
    closeMenu();
  };

  const handleCreateNew = () => {
    closeMenu();
    onCreateNew?.(inputValue);
  };

  // Already-linked students stay in the results but are shown disabled, so an
  // admin sees they exist and does not try to recreate them.
  const excludedSet = React.useMemo(() => new Set(excludeIds), [excludeIds]);

  const noResults =
    debouncedSearchValue.length >= MIN_SEARCH_LENGTH &&
    !isLoading &&
    data !== undefined &&
    students.length === 0;

  const searchAttempted =
    debouncedSearchValue.length >= MIN_SEARCH_LENGTH &&
    !isLoading &&
    data !== undefined;

  const {
    isOpen,
    getMenuProps,
    getInputProps,
    getLabelProps,
    highlightedIndex,
    getItemProps,
    openMenu,
    closeMenu,
    setHighlightedIndex,
  } = useCombobox<ApplicantDuplicate>({
    items: students,
    defaultHighlightedIndex: 0,
    selectedItem: null,
    itemToString: toStudentName,
    stateReducer(state, actionAndChanges) {
      const { changes, type, index } = actionAndChanges;
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick: {
          const newIndex =
            index !== undefined && index >= 0
              ? index
              : changes.highlightedIndex !== undefined &&
                changes.highlightedIndex >= 0
              ? changes.highlightedIndex
              : undefined;
          const candidate = newIndex !== undefined ? students[newIndex] : null;
          // Never select an already-linked (disabled) row.
          const newSelectedItem =
            candidate && excludedSet.has(candidate.id) ? null : candidate;
          return { ...changes, selectedItem: newSelectedItem };
        }
        default:
          return changes;
      }
    },
    onSelectedItemChange({ selectedItem }) {
      if (selectedItem) handleSelect(selectedItem);
    },
  });

  const openMenuUnlessDisabled = React.useCallback(() => {
    if (!isDisabled) openMenu();
  }, [isDisabled, openMenu]);

  const markdownName = React.useCallback(
    (displayName: string) => {
      if (!inputValue) return displayName;
      const escaped = inputValue.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const regex = new RegExp(`(${escaped})`, "ig");
      return displayName.replace(regex, "**$1**");
    },
    [inputValue]
  );

  const renderItem = (student: ApplicantDuplicate, isLinked: boolean) => {
    const displayName = toStudentName(student);
    const name = markdownName(displayName);
    return (
      <Flex direction="row" gap={3} width="100%" justify="space-between">
        <Flex direction="column" gap={0}>
          <Text fontSize="md">
            <Markdown>{name}</Markdown>
          </Text>
          <Text fontSize="sm" overflow="visible">
            ID: {student.id}
          </Text>
        </Flex>
        <Flex direction="column" gap={0} align="flex-end" whiteSpace="nowrap">
          {student.birth_date && (
            <Text fontSize="sm">
              Born{" "}
              {DateFns.format(
                DateFns.parse(student.birth_date, "yyyy-MM-dd", new Date()),
                "MM/dd/yyyy"
              )}
            </Text>
          )}
          {isLinked && (
            <Text fontSize="sm" color="gray.500" fontWeight="600">
              Already linked
            </Text>
          )}
        </Flex>
      </Flex>
    );
  };

  const renderLoadingItem = () => (
    <Flex direction="column" gap={0} width="100%">
      <Flex direction="row" gap={0} width="100%">
        <Skeleton height="1.25rem" width="60%" marginEnd="auto" />
        <Skeleton height="1rem" width="30%" />
      </Flex>
      <Skeleton height="1rem" width="40%" marginTop="0.5rem" />
    </Flex>
  );

  return (
    <Box position="relative" width="100%" ref={outsideRef}>
      <FormControl>
        <FormLabel {...getLabelProps()}>
          <Glossary>{label}</Glossary>
        </FormLabel>
        <InputGroup
          onClick={openMenuUnlessDisabled}
          onFocus={openMenuUnlessDisabled}
          display="flex"
          justifyContent="center"
        >
          <InputLeftElement children={<SearchIcon color="gray.500" />} />
          <Input
            {...getInputProps(
              {
                ref: inputRef,
                onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
                  setInputValue(event.target.value),
              },
              { suppressRefError: true }
            )}
            {...PASSWORD_MANAGER_IGNORE}
            value={inputValue}
            placeholder={placeholder}
            isDisabled={isDisabled}
          />
        </InputGroup>
        <UnorderedList
          {...getMenuProps({}, { suppressRefError: true })}
          display={isOpen ? "block" : "none"}
          position="absolute"
          background="white"
          marginLeft={0}
          zIndex="2000"
          boxShadow="lg"
          width="100%"
          marginTop={1}
          overflow="auto"
          maxHeight="300px"
        >
          {!noResults &&
            !isLoading &&
            students.map((student, index) => {
              const isLinked = excludedSet.has(student.id);
              return (
                <ListItem
                  {...getItemProps({
                    item: student,
                    index,
                    disabled: isLinked,
                  })}
                  key={student.id}
                  backgroundColor={
                    highlightedIndex === index ? "blackAlpha.100" : "white"
                  }
                  listStyleType="none"
                  cursor={isLinked ? "not-allowed" : undefined}
                  opacity={isLinked ? 0.6 : 1}
                  aria-disabled={isLinked}
                >
                  <Flex align="center" minHeight="16" padding="4" width="100%">
                    {renderItem(student, isLinked)}
                  </Flex>
                </ListItem>
              );
            })}
          {noResults && !isSearchError && (
            <ListItem textAlign="center" p={3}>
              <Text color="gray.500" fontSize="md">
                <Glossary>No students found</Glossary>
              </Text>
            </ListItem>
          )}
          {isSearchError && !isLoading && (
            <ListItem textAlign="center" p={3}>
              <Text color="red.600" fontSize="md">
                Something went wrong searching. Please try again.
              </Text>
            </ListItem>
          )}
          {isLoading && (
            <ListItem textAlign="center" p={2}>
              <Flex align="center" minHeight="16" padding="4">
                {renderLoadingItem()}
              </Flex>
            </ListItem>
          )}
          {searchAttempted && !isSearchError && (
            <ListItem
              borderTop="1px solid"
              borderColor="gray.100"
              listStyleType="none"
            >
              <Flex
                align="center"
                gap={2}
                padding="4"
                color="primary.500"
                cursor="pointer"
                _hover={{ backgroundColor: "blackAlpha.100" }}
                onMouseMove={() => setHighlightedIndex(-1)}
                onClick={handleCreateNew}
              >
                <RiAddLine size="1.25rem" />
                <Text fontSize="md" fontWeight="700">
                  <Glossary>Create new student</Glossary> "{inputValue}"
                </Text>
              </Flex>
            </ListItem>
          )}
          <Box ref={observerRef} height="5px" />
        </UnorderedList>
      </FormControl>
    </Box>
  );
}
